import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Check, Mail, Phone } from 'lucide-react';
import { checkPatientByMail, createInsuranceAppointment } from '../../Services/APIs/patient';
import toothlensLogo from "../../assets/illustrations/Toothlens Logo.jpeg";

const TeleDentistry = () => {
  const navigate = useNavigate();
  const { client } = useParams();
  const [showBenefits, setShowBenefits] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showVideoConsultPopup, setShowVideoConsultPopup] = useState(false);
  const [showVideoEmailPopup, setShowVideoEmailPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [videoErrorMessage, setVideoErrorMessage] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [videoPatientData, setVideoPatientData] = useState(null);
  const [videoEmail, setVideoEmail] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookedConsultation, setBookedConsultation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Listen for messages from iframe to close appointment
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === 'closeAppointment') {
        setShowAppointment(false);
        setPatientData(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const benefits = [
    {
      title: "Video Dental Consults:",
      description: "Talk to licensed dentists anytime, anywhere. Get expert advice for toothaches, gum problems, or just a second opinion."
    },
    {
      title: "Second Opinions:",
      description: "Not sure about your treatment plan or cost? Get clarity from another trusted dentist online."
    },
    {
      title: "Preventive Care Guidance:",
      description: "Tips and advice on brushing, flossing, diet, and lifestyle for a healthier smile."
    },
    {
      title: "Your Dental Report:",
      description: "Receive a personalized report after each consultation with recommendations and next steps."
    },
    {
      title: "Virtual Smile Simulation (Simmetry AI):",
      description: "See your dream smile instantly! Explore how treatments like whitening, aligners, or veneers could look on you before starting."
    },
    {
      title: "Easy Referrals (Pan India):",
      description: "If you need in-clinic treatment, we'll connect you to a trusted Toothlens dentist nearby."
    },
    {
      title: "Transparent & Affordable Pricing:",
      description: "Access dental care at rates typically 20-25% lower than standard market prices."
    },
    {
      title: "Rewards Program:",
      description: "Earn points every time you complete preventive care. Redeem points for cashback on future treatments or special wellness perks."
    }
  ];

  const serviceTypes = [
    "Book Video Consult with Dentist",
    "Request for Second Opinion",
    "Get a treatment estimate",
    "Get a Virtual Smile Simulation"
  ];

  const handleBookAppointment = () => {
    setShowBenefits(false); // Close benefits modal if open
    setShowEmailPopup(true);
    setErrorMessage('');
    setEmail('');
  };

  const handleCloseEmailPopup = () => {
    setShowEmailPopup(false);
    setEmail('');
    setErrorMessage('');
    setIsLoading(false);
  };

  const handleEmailSubmit = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('company', client);

      const response = await checkPatientByMail(formData);
      
      if (response.status === 200) {
        // Check if data array exists and has content
        if (response.data && response.data.data && response.data.data.length > 0) {
          // Patient found and has completed KYC, store patient data and redirect to appointment
          const firstPatient = response.data.data[0];
          setPatientData({
            uid: firstPatient.uid,
            name: firstPatient.name,
            email: firstPatient.email,
            mobile: firstPatient.phone_number
          });
          setShowEmailPopup(false);
          setShowAppointment(true);
        } else {
          // Data array is empty, patient hasn't completed KYC
          setErrorMessage('Almost there! Complete your registration to book an appointment.');
        }
      }
    } catch (error) {
      // API error or patient not found
      setErrorMessage('Almost there! Complete your registration to book an appointment.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAppointment = () => {
    setShowAppointment(false);
    setPatientData(null);
  };

  const handleBookVideoConsultation = () => {
    setShowBenefits(false);
    setShowVideoEmailPopup(true);
    setVideoErrorMessage('');
    setVideoEmail('');
  };

  const handleCloseVideoEmailPopup = () => {
    setShowVideoEmailPopup(false);
    setVideoEmail('');
    setVideoErrorMessage('');
    setIsLoading(false);
  };

  const handleVideoEmailSubmit = async () => {
    if (!videoEmail) {
      setVideoErrorMessage('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(videoEmail)) {
      setVideoErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setVideoErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('email', videoEmail);
      formData.append('company', client);

      const response = await checkPatientByMail(formData);
      
      if (response.status === 200) {
        if (response.data && response.data.data && response.data.data.length > 0) {
          const firstPatient = response.data.data[0];
          setVideoPatientData({
            uid: firstPatient.uid,
            name: firstPatient.name,
            email: firstPatient.email,
            mobile: firstPatient.phone_number
          });
          setShowVideoEmailPopup(false);
          setShowVideoConsultPopup(true);
        } else {
          setVideoErrorMessage('Almost there! Complete your registration to book a consultation.');
        }
      }
    } catch (error) {
      setVideoErrorMessage('Almost there! Complete your registration to book a consultation.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseVideoConsultPopup = () => {
    setShowVideoConsultPopup(false);
    setVideoPatientData(null);
    setConsultationType('');
    setSelectedDate('');
    setSelectedTime('');
  };

  const handleVideoConsultSubmit = async () => {
    if (!consultationType || !selectedDate || !selectedTime) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('client', client);
      formData.append('date', selectedDate);
      formData.append('time', selectedTime);
      formData.append('name', videoPatientData.name);
      formData.append('email', videoPatientData.email);
      formData.append('mobile', videoPatientData.mobile);
      formData.append('uid', videoPatientData.uid);
      formData.append('is_teledentistry', 'true');
      formData.append('service_type', consultationType);

      const response = await createInsuranceAppointment(formData);
      
      if (response.status === 200) {
        const consultation = {
          serviceType: consultationType,
          date: selectedDate,
          time: selectedTime,
          userDetails: videoPatientData,
          bookingId: response.data?.bookingId || `TD${Date.now().toString().slice(-6)}`
        };
        setBookedConsultation(consultation);
        setShowVideoConsultPopup(false);
        setShowSuccessModal(true);
        
        // Reset form
        setConsultationType('');
        setSelectedDate('');
        setSelectedTime('');
      } else {
        alert('Failed to book consultation. Please try again.');
      }
    } catch (error) {
      console.error('Error booking consultation:', error);
      alert('Failed to book consultation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setBookedConsultation(null);
    setVideoPatientData(null);
  };

  // Build iframe URL with patient data as query parameters
  const buildIframeUrl = () => {
    const baseUrl = `https://portal.toothlens.com/${client}-appointment`;
    if (patientData) {
      const params = new URLSearchParams({
        uid: patientData.uid,
        name: patientData.name,
        email: patientData.email,
        mobile: patientData.mobile,
        prefill: 'true'
      });
      return `${baseUrl}?${params.toString()}`;
    }
    return baseUrl;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 flex items-center justify-center p-5">
      <div className="flex gap-10 max-w-7xl w-full items-center flex-col lg:flex-row">
        
        {/* Phone Mockup */}
        <div className="relative flex justify-center w-full">
          <div className="w-[550px] h-[700px] bg-black rounded-[40px] p-4 shadow-2xl">
            <div className="w-full h-full bg-white rounded-[30px] overflow-hidden relative">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[150px] h-[25px] bg-black rounded-b-[15px] z-10"></div>
              
              {/* Phone Content */}
              <div className="px-6 py-10 h-full overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                  <img src={toothlensLogo} alt="Toothlens Logo" className="h-12 w-auto" />
                </div>

                {/* Hero Section */}
                <div className="text-center mb-10">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    TeleDental & Smile Care
                  </h1>
                  <p className="text-sm text-gray-600">
                    AI-powered dental consultations from the comfort of your home.
                  </p>
                </div>

                {/* Welcome Text */}
                <p className="text-center text-lg text-gray-700 mb-6">
                  Welcome to your AI Digital Dental Hub
                </p>

                {/* Explore Benefits Button */}
                <button
                  onClick={() => setShowBenefits(!showBenefits)}
                  className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full mb-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Explore your Benefits
                </button>

                {/* Register Section */}
                <p className="text-center text-gray-700 mb-6">
                  Register to use your benefits? Activate Now
                </p>

                {/* Register Button */}
                <button
                  onClick={() => window.open(`https://kyc.toothlens.com/group/${client}/front`, '_blank')}
                  className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full mb-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Register Now
                </button>

                {/* Already Registered Section */}
                <p className="text-center text-gray-700 mb-3">
                  Already registered? Click N Connect
                </p>

                {/* Book Video Consultation Button */}
                <button
                  onClick={handleBookVideoConsultation}
                  className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full mb-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Book Video<br />Consultation
                </button>

                {/* Search Text */}
                <p className="text-center text-gray-600 text-sm mb-6">
                  Search dentists near you, schedule instantly.
                </p>

                {/* Book Appointment Button */}
                <button
                  onClick={handleBookAppointment}
                  className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Book your Dental<br />Appointment
                </button>
              </div>
            </div>
          </div>

        </div>

      
      </div>

      {/* Benefits Popup Modal */}
      {showBenefits && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-2xl">
              <h3 className="font-bold text-xl text-gray-800">Your Benefits</h3>
              <button
                onClick={() => setShowBenefits(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Smart. Simple. Convenient.</h4>
                  <p className="text-sm text-gray-700 mb-4">
                    Everything you need for a healthier, brighter smile—right from your phone.
                    With Toothlens, taking care of your smile is now easier than ever. Here's what you get:
                  </p>
                </div>
                {benefits.map((benefit, index) => (
                  <div key={index} className="mb-3 pb-3 border-b border-gray-100 last:border-b-0">
                    <p className="mb-1">
                      <span className="font-semibold text-gray-800">{benefit.title}</span>{' '}
                      <span className="text-gray-700 text-sm">{benefit.description}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden hidden">
        {/* This is hidden as we're using phone mockup above */}
      </div>

      {/* Email Input Popup */}
      {showEmailPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Please enter your registered email</h2>
                <button
                  onClick={handleCloseEmailPopup}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
              
              {errorMessage && (
                <div className="text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleCloseEmailPopup}
                  className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailSubmit}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  {isLoading ? 'Checking...' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Iframe Modal */}
      {showAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="bg-white rounded-2xl max-w-6xl w-full h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Book Your Appointment</h2>
              <button
                onClick={handleCloseAppointment}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 p-2">
              <iframe 
                src={buildIframeUrl()}
                className="w-full h-full rounded-lg"
                title="Book Appointment"
                frameBorder="0"
                allowFullScreen
                style={{ pointerEvents: 'auto', touchAction: 'auto' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Video Consultation Email Input Popup */}
      {showVideoEmailPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Please enter your registered email</h2>
                <button
                  onClick={handleCloseVideoEmailPopup}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="videoEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="videoEmail"
                  value={videoEmail}
                  onChange={(e) => setVideoEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
              
              {videoErrorMessage && (
                <div className="text-red-600 text-sm">
                  {videoErrorMessage}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleCloseVideoEmailPopup}
                  className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVideoEmailSubmit}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  {isLoading ? 'Checking...' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Consultation Booking Form Popup */}
      {showVideoConsultPopup && videoPatientData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl sticky top-0">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Book Video Consultation</h2>
                <button
                  onClick={handleCloseVideoConsultPopup}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Patient Details */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <div>
                  <span className="text-sm text-gray-600">Name:</span>
                  <p className="font-semibold text-gray-800">{videoPatientData.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Email:</span>
                  <p className="font-semibold text-gray-800">{videoPatientData.email}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Mobile:</span>
                  <p className="font-semibold text-gray-800">{videoPatientData.mobile}</p>
                </div>
              </div>

              {/* Type of Service Dropdown */}
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                  Type of Service *
                </label>
                <select
                  id="serviceType"
                  value={consultationType}
                  onChange={(e) => setConsultationType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select service type</option>
                  {serviceTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Date Selection */}
              <div>
                <label htmlFor="consultDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="consultDate"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Time Selection */}
              <div>
                <label htmlFor="consultTime" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time *
                </label>
                <input
                  type="time"
                  id="consultTime"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleCloseVideoConsultPopup}
                  className="flex-1 py-3 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVideoConsultSubmit}
                  disabled={!consultationType || !selectedDate || !selectedTime || isSubmitting}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && bookedConsultation && (
        <div className="fixed inset-0 bg-white z-[80] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">ToothLens</h1>
              <p className="text-blue-100 text-sm">Consultation Confirmed</p>
            </div>
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <img src={toothlensLogo} alt="ToothLens Logo" className="w-8 h-8 rounded-full object-cover" />
            </div>
          </div>
          
          {/* Success Content */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 overflow-y-auto">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <Check className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-green-700 mb-2 text-center">
              Your Consultation Request has been Submitted successfully!
            </h2>
            <p className="text-green-700 mb-6 text-center">
              Our team will contact you shortly to confirm your consultation details.
            </p>
            
            {/* Consultation Details */}
            <div className="bg-gray-50 rounded-2xl p-6 w-full max-w-lg mb-6">
              <h3 className="font-bold text-gray-800 mb-3">Consultation Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600">Patient Name:</span>
                  <span className="font-semibold text-gray-800">{bookedConsultation.userDetails.name}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600">Service Type:</span>
                  <span className="font-semibold text-gray-800 text-right">{bookedConsultation.serviceType}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(bookedConsultation.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Time:</span>
                  <span className="font-semibold text-gray-800">{bookedConsultation.time}</span>
                </div>
                {/* {bookedConsultation.bookingId && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Booking ID:</span>
                    <span className="font-semibold text-gray-800">{bookedConsultation.bookingId}</span>
                  </div>
                )} */}
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="bg-blue-50 rounded-2xl p-6 w-full max-w-lg mb-6">
              <h4 className="font-semibold text-blue-800 mb-2">Your Contact Information</h4>
              <div className="space-y-2 text-sm text-blue-700">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-blue-400" />
                  <span>{bookedConsultation.userDetails.email}</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-blue-400" />
                  <span>{bookedConsultation.userDetails.mobile}</span>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-yellow-50 rounded-2xl p-6 w-full max-w-lg mb-6">
              <h4 className="font-semibold text-yellow-800 mb-2">Important Notes</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Please arrive 15 minutes before your appointment</li>
                <li>• Please show your ToothLens UID at the clinic</li>
                <li>• Please contact ToothLens for your appointment assistance</li>
              </ul>
            </div>

            {/* Close Button */}
            <button
              onClick={handleCloseSuccessModal}
              className="w-full max-w-lg bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeleDentistry;